import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, CalendarDays, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { BRAND } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "til_admin_token";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    document.title = "Admin · Tapping Into Life";
  }, []);

  const loadContacts = async (tk = token) => {
    if (!tk) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setContacts(data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Your session expired. Please log in again.");
      } else {
        toast.error(formatError(e.response?.data?.detail) || "Failed to load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadContacts(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onLogin = (tk) => {
    localStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setContacts([]);
    toast.success("Logged out.");
  };

  if (!token) {
    return <Login onSuccess={onLogin} />;
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-brand-border sticky top-0 z-10 bg-black/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/10" />
            <div>
              <div className="font-serif text-lg leading-none">Tapping Into Life</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">Admin · Submissions</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => loadContacts()}
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-brand-border px-3 py-2 rounded-full hover:border-white/40 transition"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-brand-border px-3 py-2 rounded-full hover:border-white/40 transition"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl text-white">Booking Inquiries</h1>
            <p className="text-zinc-400 mt-2">
              {loading
                ? "Loading…"
                : contacts.length === 0
                ? "No submissions yet."
                : `${contacts.length} submission${contacts.length === 1 ? "" : "s"} — newest first.`}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-brand-border" data-testid="admin-contacts-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-elevated text-zinc-400 text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-4 font-medium">When</th>
                <th className="px-5 py-4 font-medium">Name</th>
                <th className="px-5 py-4 font-medium">Email</th>
                <th className="px-5 py-4 font-medium">Phone</th>
                <th className="px-5 py-4 font-medium">Interested in</th>
                <th className="px-5 py-4 font-medium">Preferred date</th>
                <th className="px-5 py-4 font-medium">Message</th>
              </tr>
            </thead>
            <tbody>
              {loading && contacts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-zinc-500">
                    <Loader2 className="inline-block animate-spin mr-2" size={16} /> Loading submissions…
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-zinc-500">
                    No submissions yet. New inquiries from the contact form will appear here.
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`admin-row-${c.id}`}
                    className="border-t border-brand-border hover:bg-white/[0.02]"
                  >
                    <td className="px-5 py-4 text-zinc-300 whitespace-nowrap align-top">
                      {fmtDate(c.created_at)}
                    </td>
                    <td className="px-5 py-4 text-white font-medium align-top whitespace-nowrap">{c.name}</td>
                    <td className="px-5 py-4 align-top whitespace-nowrap">
                      <a
                        href={`mailto:${c.email}`}
                        className="text-brand-amber hover:underline inline-flex items-center gap-1.5"
                      >
                        <Mail size={12} /> {c.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-zinc-300 align-top whitespace-nowrap">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-white">
                          <Phone size={12} /> {c.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-5 py-4 text-zinc-300 align-top">{c.service_interest || "—"}</td>
                    <td className="px-5 py-4 text-zinc-300 align-top whitespace-nowrap">
                      {c.preferred_date ? (
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays size={12} /> {c.preferred_date}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-5 py-4 align-top max-w-md">
                      <button
                        data-testid={`admin-row-${c.id}-message`}
                        onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                        className="text-left text-zinc-300 hover:text-white"
                      >
                        {expanded === c.id ? (
                          <span className="whitespace-pre-wrap">{c.message}</span>
                        ) : (
                          <span className="line-clamp-2 text-zinc-400">{c.message}</span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      toast.success("Welcome back.");
      onSuccess(data.token);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative grain flex items-center justify-center px-6">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[640px] h-[640px] tree-halo opacity-50 pointer-events-none" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative z-10 w-full max-w-md bg-brand-surface border border-brand-border rounded-3xl p-10"
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center gap-3 mb-8 group"
        >
          <img src={BRAND.logo} alt="" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10 group-hover:ring-brand-amber transition" />
          <div className="text-left">
            <div className="font-serif text-lg leading-none">Tapping Into Life</div>
            <div className="font-accent text-brand-gold text-base leading-none mt-1">"Discovering all of you"</div>
          </div>
        </button>

        <div className="text-xs font-sans tracking-[0.22em] uppercase text-zinc-500 inline-flex items-center gap-2">
          <ShieldCheck size={14} className="text-brand-gold" /> Admin Sign-in
        </div>
        <h1 className="mt-3 font-serif text-3xl text-white">Welcome back.</h1>
        <p className="text-zinc-500 mt-2 text-sm">Sign in to view your booking inquiries.</p>

        <div className="mt-8 space-y-6">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Email</span>
            <input
              data-testid="admin-login-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@email.com"
              className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg"
            />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Password</span>
            <input
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg"
            />
          </label>
        </div>

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={submitting}
          className="mt-10 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-amber text-black font-semibold hover:bg-brand-amberHover transition disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </button>

        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-6 w-full text-center text-xs text-zinc-500 hover:text-zinc-300 transition"
        >
          ← Back to website
        </button>
      </form>
    </div>
  );
}
